import * as React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";

import type { CampaignRequestDetail, Quote } from "@/hooks/api/use-campaign-requests";

import { QuoteLinesTable } from "../components";
import { formatDateTime, isFrozen, quoteAssumptions } from "../constants";
import { EmptyState, Facts, Field, Muted, Panel } from "./parts";
import { QuoteHistory } from "./quote-history";

/**
 * The price, in the three groups it is written in, and every version that came before it.
 *
 * The groups, the per-line rates and the arithmetic all belong to `QuoteLinesTable`, which the
 * wizard's commercials step and the admin queue draw from too — a quote a client signs must read
 * identically wherever they meet it.
 *
 * Two things this tab used to say twice. The version and its dates were in the panel subtitle and
 * again on every row of the history below; and the confirmation was a green banner repeating the
 * line already under the request title. The version line is stated once, beside the title, and the
 * confirmation once, as the record it is: who, in what role, at what moment.
 */

export interface QuoteTabProps {
	request: CampaignRequestDetail;
	quotes: Quote[];
	isQuotesLoading?: boolean;
}

export function QuoteTab({ request, quotes, isQuotesLoading = false }: QuoteTabProps): React.JSX.Element {
	const quote = request.currentQuote;
	const assumptions = quoteAssumptions(quote?.assumptions);
	const history = quotes.filter((entry) => entry.id !== quote?.id);

	/**
	 * Handed over, and not yet priced. Every reason pricing can refuse is ours — a rate nobody has
	 * confirmed, a rate nobody has set, a price book that disagrees with itself about currency — so
	 * the client is told the state and not the cause, in one line.
	 */
	const awaitingPrice = !quote && !isFrozen(request.state) && request.state !== "draft";

	const confirmedBy = [request.confirmedByName, request.confirmedByRole].filter(Boolean).join(", ");

	return (
		<Stack spacing={2}>
			<Panel
				title="Your quote"
				action={
					quote ? (
						<Typography variant="caption" color="text.secondary">
							Version {quote.version} · issued {formatDateTime(quote.createdAt)}
						</Typography>
					) : null
				}
			>
				{quote ? (
					<React.Fragment>
						<QuoteLinesTable
							lines={quote.lines}
							currency={quote.currency}
							fixedTotal={quote.fixedTotal}
							variableLow={quote.variableLow}
							variableHigh={quote.variableHigh}
						/>

						{assumptions.length > 0 ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom>
									What the Estimate Assumes
								</Typography>
								<Facts>
									{assumptions.map((assumption) => (
										<Field key={assumption.key} label={assumption.label} value={assumption.value} />
									))}
								</Facts>
							</Box>
						) : null}

						{request.confirmedAt ? (
							<Muted>
								Confirmed by {confirmedBy.length > 0 ? confirmedBy : "your team"} ·{" "}
								{formatDateTime(request.confirmedAt)}
							</Muted>
						) : null}
					</React.Fragment>
				) : (
					<EmptyState>
						{awaitingPrice
							? "We are pricing this now — your quote appears here as soon as it is ready."
							: "Nothing priced yet."}
					</EmptyState>
				)}
			</Panel>

			{isQuotesLoading ? <Skeleton height={96} variant="rounded" /> : null}

			{history.length > 0 ? (
				<Panel title="Earlier versions">
					<QuoteHistory quotes={history} currentQuoteId={quote?.id} />
				</Panel>
			) : null}
		</Stack>
	);
}
